import { gsap } from 'gsap';

const DRAWER_ID = 'mainnavigation';
const MENU_ITEM_SELECTOR = '#mainnavigation .offcanvas-menu__nav > li';
const HIGHLIGHT_SELECTOR = '#mainnavigation .offcanvas-menu__highlights .project-tile, #mainnavigation .offcanvas-menu__highlights .podcast-tile';
const CTA_SELECTOR = '#mainnavigation .offcanvas-menu__cta-wrap';
const SOCIAL_LINK_SELECTOR = '#mainnavigation .offcanvas-menu__socials a';

const NAV_AT = 0.12;
const CTA_AT = 0.42;
const SOCIAL_AT = 0.55;
const HIGHLIGHTS_AT = 0.68;

const NAV_DURATION = 0.65;
const CTA_DURATION = 0.55;
const SOCIAL_DURATION = 0.5;
const HIGHLIGHTS_DURATION = 0.6;

const NAV_STAGGER = 0.06;
const SOCIAL_STAGGER = 0.035;
const HIGHLIGHTS_STAGGER = 0.07;

function prefersReducedMotion() {
  return window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

function toArray(nodeList) {
  return Array.prototype.slice.call(nodeList || []);
}

function setVisible(elements) {
  if (!elements.length) return;
  gsap.set(elements, { opacity: 1, x: 0, y: 0, clearProps: 'transform' });
}

function setHiddenFadeUp(elements) {
  if (!elements.length) return;
  gsap.set(elements, { opacity: 0, y: 30, willChange: 'transform,opacity' });
}

function setHiddenFadeRight(elements) {
  if (!elements.length) return;
  gsap.set(elements, { opacity: 0, x: 30, willChange: 'transform,opacity' });
}

function updateSiteHeaderOffsets() {
  const header = document.querySelector('.site-header');
  if (!header) return;

  const bar = header.querySelector('.site-header__bar');
  if (!bar) return;

  const headerRect = header.getBoundingClientRect();
  const barRect = bar.getBoundingClientRect();
  const headerOffset = Math.max(0, Math.ceil(barRect.bottom - headerRect.top));
  const viewportOffset = Math.max(0, Math.ceil(barRect.bottom));

  header.style.setProperty('--site-header-content-offset', `${headerOffset}px`);
  header.style.setProperty('--site-header-viewport-content-offset', `${viewportOffset}px`);
}

function initOffcanvasAnimations() {
  const drawerEl = document.getElementById(DRAWER_ID);
  updateSiteHeaderOffsets();
  window.addEventListener('resize', updateSiteHeaderOffsets);

  if (!drawerEl) return;

  let tl = null;

  const getTargets = () => {
    const menuItems = toArray(drawerEl.querySelectorAll(MENU_ITEM_SELECTOR));
    const highlights = toArray(drawerEl.querySelectorAll(HIGHLIGHT_SELECTOR));
    const cta = toArray(drawerEl.querySelectorAll(CTA_SELECTOR));
    const socialLinks = toArray(drawerEl.querySelectorAll(SOCIAL_LINK_SELECTOR));
    return { menuItems, highlights, cta, socialLinks };
  };

  const reset = () => {
    if (tl) {
      tl.kill();
      tl = null;
    }
    const { menuItems, highlights, cta, socialLinks } = getTargets();
    setVisible(menuItems.concat(highlights, cta, socialLinks));
  };

  const onOpen = () => {
    updateSiteHeaderOffsets();

    const { menuItems, highlights, cta, socialLinks } = getTargets();
    const targets = menuItems.concat(highlights, cta, socialLinks);

    if (!targets.length) return;

    if (prefersReducedMotion()) {
      reset();
      return;
    }

    if (tl) {
      tl.kill();
      tl = null;
    }

    // Align with existing GSAP reveal defaults and avoid fighting the drawer slide transition.
    setHiddenFadeRight(menuItems.concat(highlights));
    setHiddenFadeUp(cta.concat(socialLinks));

    tl = gsap.timeline({ defaults: { ease: 'power2.out' } });

    tl.to(cta, {
      opacity: 1,
      y: 0,
      duration: CTA_DURATION,
      clearProps: 'willChange',
      immediateRender: false
    }, CTA_AT);

    tl.to(socialLinks, {
      opacity: 1,
      y: 0,
      duration: SOCIAL_DURATION,
      stagger: SOCIAL_STAGGER,
      clearProps: 'willChange',
      immediateRender: false
    }, SOCIAL_AT);

    tl.to(menuItems, {
      opacity: 1,
      x: 0,
      duration: NAV_DURATION,
      stagger: NAV_STAGGER,
      clearProps: 'willChange',
      immediateRender: false
    }, NAV_AT);

    tl.to(highlights, {
      opacity: 1,
      x: 0,
      duration: HIGHLIGHTS_DURATION,
      stagger: HIGHLIGHTS_STAGGER,
      clearProps: 'willChange',
      immediateRender: false
    }, HIGHLIGHTS_AT);
  };

  drawerEl.addEventListener('drawerIsOpen', onOpen);
  drawerEl.addEventListener('drawerIsClose', reset);
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initOffcanvasAnimations);
} else {
  initOffcanvasAnimations();
}
